import { app_domain, getShareHost } from 'app/config/constants'
import { i18n } from 'i18n'
import { getLang } from 'i18n/config/LanguageDetector'
import { makeAutoObservable } from 'mobx'
import { PoweredOptions } from 'store/PoweredOptions'
import { IdentityMode } from 'type/Identity'
import { extractModes, Measurements, MEASUREMENTS_DEFAULT, Meta, Options, Photo, Share, Theme, Turn } from 'type/Options'

export const STAND_BANNER = false

class OptionsStore {
  options: Options = {}
  meta: Meta = {}

  constructor() {
    makeAutoObservable(this)
  }

  init(options: Options, meta: Meta) {
    this.options = options
    this.meta = meta
  }

  get product(): string {
    return this.options.product!
  }

  get company(): string {
    return this.options.company!
  }

  get f4(): boolean {
    return this.company === 'fe281ed8-03f2-4928-8f6e-4909efb13b8c'
  }

  get t2(): boolean {
    return this.company === 'fa46f5ab-ac8e-48c3-87dd-5db492ec9014'
      || this.product === '5e6a8220-df21-43fe-ae63-d9dd34b756be'
  }

  get irnby(): boolean {
    return this.company === 'd2596011-9aba-4e18-809f-bed7cfe5e6f7'
      || this.product === '40d67d55-d264-4262-81d9-f71ac6933d11'
  }

  get ostin(): boolean {
    return this.company === '950a942d-5f1c-4bdd-a40f-67bd291175e8'
      || this.product === '3ddc327a-17d2-4e13-876f-c49fd1cc09c7'
  }

  get lili_blanc(): boolean {
    return this.company === '4f556130-c099-4e15-b73d-42ec37872b4a'
      || this.product === '9fbf06e6-d2d8-4f92-a856-605970f79319'
  }

  get sela(): boolean {
    return this.company === '1e2f902b-c114-4dcc-a6cc-5e37a612c2be'
      || this.product === 'a59956c4-d95f-4668-96ed-0808485e69c'
  }

  get sochi(): boolean {
    return this.company === '346f34ab-7a92-43cd-8a18-6e79d382da70'
      || this.company === 'abc132ec-2353-4d8b-a60d-4c103db81066'
      || this.product === '95e684b9-0d2a-4b0b-97d8-ac7a272f664e'
  }

  get foo(): boolean {
    return this.company === '519e6622-fb56-442a-ab19-ff69eef71ed9'
      || this.company === 'f5069533-2038-4117-b9ad-da0ca83cb27d'
  }

  get salaris(): boolean {
    return this.company === 'dccc94fc-49cf-4893-8073-c3c7154d6eb2'
  }

  get likes(): boolean {
    return !this.stand && !!this.options.likes?.on
  }

  get prices(): boolean {
    const lang = getLang()
    return lang === 'ru' && !this.stand
  }

  get tsum(): boolean {
    return !!this.options.tsum
  }

  get upper(): boolean {
    return !this.tsum
  }

  get inline(): boolean {
    return !!this.options.inline
  }

  get telegram(): boolean {
    return !!this.options.telegram
  }

  get showMin(): boolean {
    return !this.inline && !this.telegram && !this.webview
  }

  get external(): boolean {
    return !!this.options.external
  }

  get stand(): boolean {
    return !!this.options.stand || !!this.options.basic
  }

  get stand2(): boolean {
    return !!this.options.stand && !this.options.basic
  }

  get basic(): boolean {
    return !!this.options.basic
  }

  get single(): boolean {
    return this.options.single_profile !== false
  }

  get boutique(): boolean {
    return !!this.options.boutique
  }

  get camera(): boolean {
    return !!this.options.camera || !!this.stand
  }

  get atelier(): boolean {
    return app_domain === 'atelier'
  }

  get mirror(): boolean {
    return true
  }

  get turn(): Turn {
    const angle = this.options?.turn ?? 0
    const positive = (360 + angle % 360) % 360
    const x = Math.round(positive / 90)
    return 90 * x as Turn
  }

  get qr_upload(): boolean {
    return this.stand && !!this.options.qr_photo
  }

  get powered(): PoweredOptions | undefined {
    if (this.basic) return undefined
    return PoweredOptions.fromOptions(this.options)
  }

  get footer(): boolean {
    return !opt.stand
  }

  get body(): IdentityMode[] {
    return extractModes(this.options)
  }

  get measurements(): Measurements[] {
    const measurements = this.options.measurements
    return measurements?.length ? measurements : MEASUREMENTS_DEFAULT
  }

  get photo(): Photo[] {
    if (this.meta.selfie) return ['selfie']
    if (this.basic) {
      const photo = (this.options?.profile_photo ?? []).filter(it => it === 'selfie' || it === 'full')
      return photo.length ? photo : ['full']
    }
    const photo = this.options?.profile_photo ?? []
    if (photo.length) return photo
    return ['full']
  }

  get photo_def(): Photo {
    return this.photo.includes('full') ? 'full' : opt.photo[0] || 'full'
  }

  get share(): Set<Share> {
    const share = this.options?.share ?? []
    if (share?.length) return new Set(share)
    return new Set(['looky', 'telegram', 'whatsapp'])
  }

  get share_host(): string {
    if (this.sela) return 'https://ai-fit.sela.ru'
    return getShareHost()
  }

  get buy_single(): boolean {
    return this.options?.buy_single !== false
  }

  get buy_combo(): boolean {
    return this.options?.buy_combo !== false
  }

  get buy_title(): string {
    return this.options?.buy_title?.trim() || i18n('Buy')
  }

  get utm_buy(): string {
    return this.options?.utm_buy?.trim() || ''
  }

  get sizes(): boolean {
    return !this.irnby && this.options.show_clothing_sizes === true
  }

  get combine_full(): boolean {
    return !!this.options.combine_full
  }

  get single_gen(): boolean {
    return !!this.options.single_gen
  }

  get square(): boolean {
    return this.irnby
  }

  get close_back(): boolean {
    return this.webview
  }

  get webview(): boolean {
    return !!this.options.webview
  }

  get theme(): Theme {
    return this.options.theme || 'auto'
  }

  radius(radius: number): number
  radius(radius: number | null | undefined): number | undefined
  radius(radius: number): number {
    return this.square ? 0 : radius
  }
}

export const opt = new OptionsStore()
